import { type FC, useEffect, useRef, useState } from 'react';
import { Button } from '../Button/Button';
import { FormFieldError } from '../FormFieldError/FormFieldError';
import { IconButton } from '../IconButton/IconButton';
import { Message } from '../Message/Message';
import { TextInput } from '../TextInput/TextInput';
import { ENTER, TAB } from '../utils/keyboard/keyboardKeys';
import { IPAddressList } from './IPAddressList';
import {
  type IPSubnetForm,
  type IPSubnetInputProps,
  type IPSubnetInputValue,
  type IPSubnetInputValueOrDraft,
  type IPSubnetInputValueWithError,
  type ValidationResult,
  hasIPSubnetInputValueError,
} from './IPSubnetInput.types';
import {
  extractIpAddresses,
  mergeAndSortUniqueIpAddresses,
  serializeIpAddresses,
} from './helpers/ipAddressHelpers';
import {
  isValidIPv4,
  mergeValidationResults,
  validateByType,
} from './helpers/ipAddressValidations';
import { filterIPv4IPs } from './helpers/searchIPv4';
import { filterIPv6IPs } from './helpers/searchIPv6';

export const IPSubnetInput: FC<IPSubnetInputProps> = ({
  value,
  warning,
  type = 'ip/cidr',
  searchable = true,
  onChange,
  customValidation,
  onValidate,
  ...props
}) => {
  // represents the current value of the component (localValue.ips) enriched with invalid user input (localValue.userInput), if any
  const [localValue, setLocalValue] =
    useState<IPSubnetInputValueOrDraft>(value);
  // represents the current value of the input field
  const [ipAddressValue, setIpAddressValue] = useState(
    hasIPSubnetInputValueError(value) ? value.invalidInput : '',
  );

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchedAddresses, setSearchedAddresses] = useState<string[]>([]);

  const isSubmitting = useRef(false);

  const addresses = localValue.ips;

  useEffect(() => {
    if (searchValue) {
      const matchResults = isValidIPv4(searchValue)
        ? filterIPv4IPs(searchValue, addresses)
        : filterIPv6IPs(searchValue, addresses);

      setSearchedAddresses(matchResults);
    }
  }, [searchValue, addresses]);

  useEffect(() => {
    setLocalValue((prevState) => (prevState !== value ? value : prevState));
  }, [value]);

  const handleIpAddressSubmit = (data: IPSubnetForm) => {
    const parsedAddresses = extractIpAddresses(data.ipAddress);

    // Validate the parsed addresses
    const { message, validAddresses, invalidAddresses } =
      validateIpAddresses(parsedAddresses);

    // Update relevant UI inputs' states
    if (message !== undefined) {
      setIpAddressValue(serializeIpAddresses(invalidAddresses, true));
    } else {
      setIpAddressValue('');
      setSearchValue('');
    }

    // Set the local value state and notify the parent component about the change
    setLocalValue((prevState) => {
      const mergedAddresses = mergeAndSortUniqueIpAddresses(
        prevState.ips,
        validAddresses,
      );

      const newLocalValue =
        message !== undefined
          ? ({
              ips: mergedAddresses,
              invalidInput: serializeIpAddresses(invalidAddresses),
              message: message,
            } satisfies IPSubnetInputValueWithError)
          : ({
              ips: mergedAddresses,
            } satisfies IPSubnetInputValue);

      // Notify the parent component about the change after the localValue state is updated
      setTimeout(() => {
        onChange(newLocalValue);
      }, 0);

      return newLocalValue;
    });
  };

  const handleIpAddressRemoving = (tags: string[]) => {
    // Construct a new local value by removing the specified tags from the current state and notify the parent component about the change
    setLocalValue((prevState) => {
      const newLocalValue = {
        ...prevState,
        ips: prevState.ips.filter((tag) => !tags.includes(tag)),
      } satisfies IPSubnetInputValueOrDraft;

      // Notify the parent component about the change after the localValue state is updated
      setTimeout(() => {
        onChange(newLocalValue);
      }, 0);

      return newLocalValue;
    });
  };

  const handleTextInputBlur = (rawString: string) => {
    const tempValue =
      rawString.length > 0
        ? ({
            ips: localValue.ips,
            invalidInput: rawString,
            message:
              'Press “Add to list” button to add values or clear the input.',
          } satisfies IPSubnetInputValueOrDraft)
        : ({
            ips: localValue.ips,
          } satisfies IPSubnetInputValue);

    // Enforce the form validation mechanism to trigger and display the error message
    onChange(tempValue);
  };

  const validateIpAddresses = (addresses: string[]): ValidationResult => {
    const builtInValidationResult = validateByType(addresses, type);
    const customValidationResult = customValidation?.(
      builtInValidationResult.validAddresses,
    );

    return mergeValidationResults(
      builtInValidationResult,
      customValidationResult,
    );
  };

  const renderIPAddressList = () => {
    if (addresses.length === 0) {
      return null;
    }

    if (searchValue && searchedAddresses.length > 0) {
      return (
        <IPAddressList
          addresses={searchedAddresses}
          disabled={props.disabled}
          name={props.name}
          removeAddresses={handleIpAddressRemoving}
        />
      );
    }

    if (searchValue && searchedAddresses.length === 0) {
      return (
        <IPAddressList
          addresses={searchedAddresses}
          disabled={props.disabled}
          name={props.name}
          removeAddresses={handleIpAddressRemoving}
          infoMessage={
            <div className='text-xs'>{`No results found for "${searchValue}"`}</div>
          }
        />
      );
    }

    return (
      <IPAddressList
        addresses={addresses}
        disabled={props.disabled}
        name={props.name}
        removeAddresses={handleIpAddressRemoving}
      />
    );
  };

  return (
    <div className='flex flex-col gap-xs'>
      <div className='flex items-end gap-2xs'>
        <TextInput
          value={ipAddressValue}
          onChange={(e) => {
            setIpAddressValue(e.target.value);
          }}
          className='flex-1 w-full'
          placeholder={props.placeholder}
          type='text'
          onKeyDown={(e) => {
            if (e.key === ENTER || e.key === TAB) {
              handleIpAddressSubmit({
                ipAddress: ipAddressValue,
                search: searchValue,
              });
              if (e.key === ENTER) {
                // When the Tab key is pressed, the transition of focus should still happen.
                // Enter key should be completely swallowed to prevent form submission.
                e.preventDefault();
                e.stopPropagation();
              }
            }
          }}
          onBlur={() => {
            if (!isSubmitting.current) {
              handleTextInputBlur(ipAddressValue);
            }
            isSubmitting.current = false;
          }}
          onClear={() => {
            isSubmitting.current = true;
            handleIpAddressSubmit({
              ipAddress: '',
              search: searchValue,
            });
          }}
          hasError={props.hasError || props.error !== undefined}
          disabled={props.disabled}
          name={props.name}
          id={props.inputId}
        />
        <Button
          variant='outline'
          // Using `onMouseDown` intentionally to prevent the `handleTextInputBlur` handler from being called
          onMouseDown={() => {
            isSubmitting.current = true;
            handleIpAddressSubmit({
              ipAddress: ipAddressValue,
              search: searchValue,
            });
          }}
          disabled={props.disabled}
        >
          Add to list
        </Button>
        {addresses.length > 0 && searchable ? (
          !isSearchActive ? (
            <IconButton
              onClick={() => {
                setIsSearchActive(true);
              }}
              variant='outline'
              icon='fa-search'
              disabled={props.disabled}
              type='button'
            />
          ) : (
            <TextInput
              type='search'
              name='search'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={() => {
                if (searchValue === '') {
                  setIsSearchActive(false);
                }
              }}
              autoFocus
              disabled={props.disabled}
            />
          )
        ) : null}
      </div>
      <FormFieldError error={props.error} />
      {renderIPAddressList()}
      {warning && (
        <Message
          type='warning'
          variant='plain'
          size='small'
          title={warning}
          className='mt-2'
        />
      )}
    </div>
  );
};
